// « Josaphat a aimé cette publication » — la ligne au-dessus d'une carte.
//
// ─────────────────────────────────────────────────────────────────────────────
// POURQUOI CE FICHIER EXISTE
//
// Demandé par Josaphat le 18 août 2026 : « quand quelqu'un aime une publication
// et qu'un proche voit la même publication, on voit par exemple Josaphat a aimé
// cette publication ». C'est ce que Facebook appelle la preuve sociale : ce
// n'est pas un ornement, c'est ce qui fait qu'on s'arrête sur une carte.
//
// ⚠️ ELLE NE COÛTE AUCUNE LECTURE, ET C'EST TOUT LE DESSIN.
// Savoir lequel de vos amis a réagi à chacune des vingt publications du fil, ce
// serait vingt requêtes à chaque ouverture. Les identifiants voyagent donc AVEC
// la publication — `derniersAmens`, `derniersCommentateurs` — et la liste de vos
// amis est déjà en mémoire. Il ne reste qu'une intersection, faite ici.
//
// ⚠️ ON N'Y MET QUE SOI-MÊME, ET LE SERVEUR LE VÉRIFIE. Sans cela, n'importe
// qui écrirait l'identifiant d'un autre et cette ligne mentirait à toute la
// communauté. Voir `firestore.rules`, fonction `seulementMoiDans`.
//
// ⚠️ LE MÊME COMPOSANT EXISTE SUR LE TÉLÉPHONE — `koinonia-native/composants/
// EchoDuCercle.jsx`. Les deux doivent dire la même chose, mot pour mot.

import { useMembre } from "./hooks.js";

/** Ceux de mes amis qui sont dans cette liste — au plus deux, on n'en dit pas plus. */
function amisDedans(liste, amis, moiUid) {
  if (!liste?.length || !amis) return [];
  return liste.filter((u) => u !== moiUid && amis.has?.(u)).slice(0, 2);
}

/* Un nom se lit sur la fiche, jamais dans la liste : celle-ci ne porte que des
   identifiants — précisément pour qu'on ne puisse pas s'y donner le nom d'un
   autre. `useMembre` garde ce qu'il a déjà lu, et ce sont des AMIS : leurs
   fiches sont presque toujours déjà là. */
function Nom({ ctx, uid }) {
  const membre = useMembre(ctx, uid);
  return (
    <a className="echo__nom" href={`#/profil/${uid}`}>
      {membre.nom}
    </a>
  );
}

export default function EchoDuCercle({ p, ctx }) {
  const signaux = ctx?.signaux?.instantane?.();
  const amis = signaux?.amis;
  const moiUid = ctx?.moi?.uid;

  const ontAime = amisDedans(p?.derniersAmens, amis, moiUid);
  const ontCommente = amisDedans(p?.derniersCommentateurs, amis, moiUid);

  /* ⚠️ AIMER PASSE AVANT COMMENTER, et l'on n'affiche qu'une ligne. Deux
     lignes de contexte au-dessus d'une carte repoussent la publication
     elle-même hors de l'écran — or c'est elle qu'on est venu lire. */
  const uids = ontAime.length ? ontAime : ontCommente;
  if (!uids.length) return null;

  const verbe = ontAime.length
    ? uids.length > 1 ? "ont aimé" : "a aimé"
    : uids.length > 1 ? "ont commenté" : "a commenté";

  return (
    <p className="echo petit texte-doux">
      {uids.map((uid, i) => (
        <span key={uid}>
          {i > 0 ? " et " : ""}
          <Nom ctx={ctx} uid={uid} />
        </span>
      ))}{" "}
      {verbe} cette publication
    </p>
  );
}
